// Fix: Added explicit type casting in handleFileSelect to resolve unknown type issues with FileList items.

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { 
  LayoutDashboard, Users, Calendar, Mail, TrendingUp, Sparkles, LogOut, Loader2, 
  RefreshCw, CheckCircle, Clock, BookOpen, Send, ShieldCheck, Image as ImageIcon, 
  X, Trash2, MessageSquare, MessageCircle, Video, Search, Youtube, Activity, Video as VideoIcon, 
  User as UserIcon, Camera, Save, Settings, MapPin, Phone, Bell, Check, XCircle, 
  Timer, Plus, Layers, DollarSign, PlayCircle, StopCircle, PhoneCall, ArrowLeft, 
  BarChart3, Globe, ExternalLink, Heart, Sun, Moon, Orbit, Paperclip, Link, UploadCloud, 
  FileText, Play
} from 'lucide-react';
import { 
  supabase, fetchAllBookings, fetchMemberships, fetchBlogs, 
  fetchAllMessagesForAdmin, sendMessage, getAdminStatus, setAdminStatus, 
  fetchExclusiveContent, createExclusiveContent, saveSessionLog, 
  startLiveSession, endLiveSession, calculateBirthChart 
} from '../services/geminiService';
import { BookingRecord, MemberRecord, BlogPost, Message, ExclusiveContent, ChartResult, BirthData } from '../types';
import LiveVideoRoom from './LiveVideoRoom';

interface AdminDashboardProps {
  onExit: () => void;
}

interface PresencePayload {
  status: 'online' | 'away';
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onExit }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'bookings' | 'members' | 'messages' | 'vault'>('overview');
  const [data, setData] = useState<{
    bookings: BookingRecord[];
    members: MemberRecord[];
    messages: Message[];
    vault: ExclusiveContent[];
  }>({ bookings: [], members: [], messages: [], vault: [] });

  const [deskStatus, setDeskStatus] = useState<'online' | 'away'>('away');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [selectedMember, setSelectedMember] = useState<MemberRecord | null>(null);
  const [memberChart, setMemberChart] = useState<ChartResult | null>(null);
  const [loadingMemberChart, setLoadingMemberChart] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [activeCall, setActiveCall] = useState<{ type: 'audio' | 'video', email: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAddingVaultItem, setIsAddingVaultItem] = useState(false);
  const [newVaultItem, setNewVaultItem] = useState({
    title: '',
    description: '',
    content: '',
    type: 'album' as 'blog' | 'album' | 'video',
    price: 15,
    media_urls: [] as string[],
    video_url: ''
  });
  const [isSavingVault, setIsSavingVault] = useState(false);

  const adminProfile = {
    name: 'Allie Pak',
    title: 'High Priestess of the Cosmos',
    profile_picture: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400',
    bio: 'Professional astrologer.',
    email: 'allie.pakele@gmail.com'
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookings, memberships, messages, status, vault] = await Promise.all([
        fetchAllBookings(),
        fetchMemberships(),
        fetchAllMessagesForAdmin(),
        getAdminStatus(),
        fetchExclusiveContent()
      ]);
      setData({ 
        bookings: (bookings || []) as BookingRecord[], 
        members: (memberships || []) as MemberRecord[],
        messages: (messages || []) as Message[],
        vault: (vault || []) as ExclusiveContent[]
      });
      setDeskStatus(status);
    } catch (e) { 
      console.error("Data fetch error", e); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Symphony95') {
      setIsAuthenticated(true);
      loadData();
    } else { 
      setLoginError('Invalid signature.'); 
      setPassword(''); 
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    const presenceSub = supabase.channel('admin-presence-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_presence', filter: 'id=eq.allie_main' }, (payload) => {
        const newData = payload.new as PresencePayload;
        if (newData && newData.status) {
          setDeskStatus(newData.status);
        }
      })
      .subscribe();

    const messagesSub = supabase.channel('admin-messages-sync')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        setData(prev => ({ ...prev, messages: [...prev.messages, payload.new as Message] }));
      })
      .on('broadcast', { event: 'call-signal' }, ({ payload }: any) => {
        if (payload.type === 'initiate' && payload.receiver === 'admin') {
          setActiveCall({ type: payload.callType, email: payload.senderEmail });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(presenceSub);
      supabase.removeChannel(messagesSub);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeChatId) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data.messages, activeChatId]);

  const handleToggleStatus = async () => {
    if (isUpdatingStatus) return;
    setIsUpdatingStatus(true);
    const newStatus = deskStatus === 'online' ? 'away' : 'online';
    try {
      await setAdminStatus(newStatus);
      setDeskStatus(newStatus);
    } catch (e: any) { 
      console.error("Status update error:", e);
      alert(`Celestial presence update failed.`); 
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !activeChatId || isSendingReply) return;
    setIsSendingReply(true);
    try {
      await sendMessage({ sender_id: 'admin', receiver_id: activeChatId, content: replyText });
      setReplyText('');
    } catch (err) {
      alert("Failed to send transmission.");
    } finally {
      setIsSendingReply(false);
    }
  };

  const initiateCall = (type: 'audio' | 'video') => {
    if (!activeChatId) return;
    supabase.channel('admin-messages-sync').send({
      type: 'broadcast', event: 'call-signal',
      payload: { type: 'initiate', callType: type, senderEmail: 'admin', receiver: activeChatId }
    });
    setActiveCall({ type, email: activeChatId });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    // Fix: Explicitly cast to File[] to satisfy compiler that size, name, and methods exist on the objects.
    (Array.from(files) as File[]).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewVaultItem(prev => ({ ...prev, media_urls: [...prev.media_urls, reader.result as string] }));
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImagePreview = (index: number) => {
    setNewVaultItem(prev => ({ ...prev, media_urls: prev.media_urls.filter((_, i) => i !== index) }));
  };

  const handleAddVaultItem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newVaultItem.type !== 'video' && newVaultItem.media_urls.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    if (newVaultItem.type === 'video' && !newVaultItem.video_url.includes('youtube')) {
      alert("Please provide a valid YouTube URL.");
      return;
    }

    setIsSavingVault(true);
    try {
      await createExclusiveContent({ ...newVaultItem });
      setIsAddingVaultItem(false);
      setNewVaultItem({ title: '', description: '', content: '', type: 'album' as 'blog' | 'album' | 'video', price: 15, media_urls: [], video_url: '' });
      loadData();
    } catch (err) {
      alert("Failed to secure vault item.");
    } finally {
      setIsSavingVault(false);
    }
  };

  const handleSessionAction = async (booking: BookingRecord, action: 'start' | 'end') => {
    try {
      if (action === 'start') await startLiveSession(booking.id);
      else await endLiveSession(booking.id);
      loadData();
    } catch (err) {
      alert("Session transmission failed.");
    }
  };

  const handleViewMemberDetails = (member: MemberRecord) => {
    setSelectedMember(member);
    setMemberChart(null);
  };

  const handleGenerateMemberChart = async () => {
    if (!selectedMember || !selectedMember.birth_date) return;
    setLoadingMemberChart(true);
    try {
      const birthData: BirthData = {
        name: selectedMember.name, date: selectedMember.birth_date,
        time: selectedMember.birth_time || '12:00', location: selectedMember.birth_location || 'Unknown'
      };
      const chart = await calculateBirthChart(birthData, 'en');
      setMemberChart(chart);
    } catch (err) {
      alert("Failed to calculate member chart.");
    } finally {
      setLoadingMemberChart(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return data.members;
    const query = memberSearch.toLowerCase();
    return data.members.filter(m => m.name.toLowerCase().includes(query) || (m.email || '').toLowerCase().includes(query));
  }, [data.members, memberSearch]);

  const conversations = useMemo(() => {
    const map = new Map<string, Message[]>();
    (data.messages || []).forEach(m => {
      const otherId = m.sender_id === 'admin' ? m.receiver_id : m.sender_id;
      if (!map.has(otherId)) map.set(otherId, []);
      map.get(otherId)?.push(m);
    });
    return Array.from(map.entries()).sort((a, b) => {
      const lastA = a[1][a[1].length - 1].created_at;
      const lastB = b[1][b[1].length - 1].created_at;
      return new Date(lastB).getTime() - new Date(lastA).getTime();
    });
  }, [data.messages]);

  const activeMessages = useMemo(() => {
    if (!activeChatId) return [];
    return (data.messages || []).filter(m => m.sender_id === activeChatId || m.receiver_id === activeChatId);
  }, [data.messages, activeChatId]);

  const menuItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' }, 
    { id: 'bookings', icon: Calendar, label: 'Bookings' }, 
    { id: 'members', icon: Users, label: 'Members' }, 
    { id: 'messages', icon: MessageSquare, label: 'Messages' },
    { id: 'vault', icon: Layers, label: 'Vault' }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-slate-900 border border-amber-500/20 p-10 rounded-sm text-center shadow-2xl animate-fade-in">
          <div className="p-3 bg-amber-500 text-slate-950 rounded-sm w-fit mx-auto mb-8"><ShieldCheck size={32}/></div>
          <h2 className="text-2xl font-serif text-white mb-6 uppercase tracking-widest leading-tight">Admin Gate</h2>
          <form onSubmit={handleLogin} className="space-y-6 text-left">
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-indigo-400 font-bold ml-1">Universal Key</label>
              <input required type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-950 border border-indigo-900 p-4 text-indigo-100 rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="••••••••" />
            </div>
            {loginError && <p className="text-rose-500 text-[10px] uppercase font-bold text-center bg-rose-500/10 py-2 rounded-sm border border-rose-500/20">{loginError}</p>}
            <button type="submit" className="w-full py-5 bg-amber-500 text-slate-950 font-bold uppercase text-[10px] tracking-widest hover:bg-amber-400 transition-all rounded-sm shadow-lg active:scale-95 flex items-center justify-center gap-2">Verify Identity</button>
          </form>
          <button onClick={onExit} className="mt-8 text-indigo-500 text-[10px] uppercase tracking-[0.3em] hover:text-white transition-colors">Back to Temple</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col md:flex-row h-screen overflow-hidden">
      {activeCall && <LiveVideoRoom isAdmin={true} memberEmail={activeCall.email} audioOnly={activeCall.type === 'audio'} onClose={() => setActiveCall(null)} />}

      <aside className="hidden md:flex w-64 bg-slate-900 border-r border-indigo-900/50 p-6 flex-col shrink-0">
        <div className="flex items-center gap-3 mb-10"><div className="p-2 bg-amber-500 text-slate-950 rounded-sm"><Sparkles size={20} /></div><span className="font-serif text-lg tracking-widest uppercase text-white">Sanctum</span></div>
        <nav className="flex-1 space-y-2">
          {menuItems.map(item => (
            <button key={item.id} onClick={() => { setActiveTab(item.id as any); setSelectedMember(null); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-xs uppercase tracking-widest transition-all ${activeTab === item.id ? 'bg-amber-500 text-slate-950 font-bold shadow-lg' : 'text-indigo-400 hover:bg-indigo-950'}`}>
              <item.icon size={16} /> {item.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-indigo-900/50 space-y-4">
           <div className={`p-4 rounded-sm border transition-all ${deskStatus === 'online' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
              <div className="flex justify-between items-center mb-2"><span className="text-[10px] uppercase font-bold text-indigo-400">Desk Status</span><div className={`w-2.5 h-2.5 rounded-full ${deskStatus === 'online' ? 'bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]' : 'bg-rose-500'}`} /></div>
              <button disabled={isUpdatingStatus} onClick={handleToggleStatus} className={`w-full py-3 text-[10px] uppercase font-bold rounded-sm transition-all flex items-center justify-center gap-2 shadow-lg ${deskStatus === 'online' ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}>
                {isUpdatingStatus ? <Loader2 size={12} className="animate-spin" /> : deskStatus === 'online' ? 'Go Offline' : 'Go Online'}
              </button>
           </div>
           <button onClick={onExit} className="w-full flex items-center gap-3 px-4 py-3 border border-indigo-900/50 text-indigo-500 hover:text-white rounded-sm text-xs uppercase tracking-widest transition-all"><LogOut size={16} /> Exit Sanctum</button>
        </div>
      </aside>

      {/* ADMIN MOBILE BOTTOM NAV */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[1000001] bg-[#020617] border-t border-amber-500/40 shadow-[0_-8px_30px_rgba(0,0,0,0.9)] pb-[env(safe-area-inset-bottom, 0)] h-20 flex justify-around items-center px-2">
        {menuItems.map((item) => (
          <button 
            key={item.id} 
            onClick={() => setActiveTab(item.id as any)} 
            className={`flex flex-col items-center justify-center gap-1.5 px-3 transition-all ${activeTab === item.id ? 'text-amber-500 scale-110' : 'text-indigo-200 active:text-amber-400'}`}
          >
            <item.icon size={22} className={activeTab === item.id ? 'drop-shadow-[0_0_10px_rgba(245,158,11,0.6)]' : 'drop-shadow-[0_0_10px_rgba(129,140,248,0.3)]'} />
            <span className="text-[9px] uppercase tracking-[0.2em] font-bold">{item.label}</span>
          </button>
        ))}
        <button onClick={onExit} className="flex flex-col items-center justify-center gap-1.5 px-3 text-indigo-200 active:text-rose-500">
           <LogOut size={22} />
           <span className="text-[9px] uppercase tracking-[0.2em] font-bold">Exit</span>
        </button>
      </nav>

      <main className="flex-1 flex flex-col overflow-hidden relative">
        <header className="p-6 md:p-8 border-b border-indigo-900/30 flex justify-between items-center bg-slate-950 shrink-0">
          <div className="flex items-center gap-4"><div className="w-12 h-12 rounded-full border border-amber-500/50 overflow-hidden bg-indigo-950 flex items-center justify-center"><img src={adminProfile.profile_picture} className="w-full h-full object-cover" /></div><div><h1 className="text-xl font-serif text-white uppercase tracking-widest">{adminProfile.name}</h1><div className="flex items-center gap-2"><span className={`w-1.5 h-1.5 rounded-full ${deskStatus === 'online' ? 'bg-emerald-500' : 'bg-rose-500'}`} /><p className="text-indigo-400 text-[10px] uppercase tracking-[0.2em]">{deskStatus === 'online' ? 'Observing Heavens' : 'Offline'}</p></div></div></div>
          <button onClick={loadData} className="p-3 text-indigo-400 hover:text-amber-400 transition-all"><RefreshCw size={20} className={loading ? 'animate-spin' : ''} /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar pb-32 md:pb-8">
          {activeTab === 'vault' && (
            <div className="space-y-8 animate-fade-in pb-20">
              <div className="flex justify-between items-end border-b border-indigo-900/30 pb-6">
                <h3 className="text-3xl font-serif text-white uppercase tracking-widest">Exclusive Vault</h3>
                <button onClick={() => setIsAddingVaultItem(true)} className="px-6 py-3 bg-amber-500 text-slate-950 text-[10px] uppercase font-bold tracking-widest rounded-sm flex items-center gap-2 hover:bg-amber-400 shadow-lg"><Plus size={16}/> New Asset</button>
              </div>

              {isAddingVaultItem && (
                <div className="bg-slate-900 border border-amber-500/30 p-8 rounded-sm animate-fade-in shadow-2xl mb-8">
                  <div className="flex justify-between items-center mb-8"><h4 className="text-xl font-serif text-white uppercase tracking-widest">Forge Vault Asset</h4><button onClick={() => setIsAddingVaultItem(false)} className="text-indigo-500 hover:text-white"><X size={20}/></button></div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-8">
                     {[
                       { id: 'blog', icon: FileText, label: 'Wisdom Transmission' },
                       { id: 'album', icon: ImageIcon, label: 'Celestial Gallery' },
                       { id: 'video', icon: Play, label: 'Universal Frequency' }
                     ].map(type => (
                       <button key={type.id} onClick={() => setNewVaultItem({...newVaultItem, type: type.id as any})} className={`flex flex-col items-center gap-3 p-4 border rounded-sm transition-all ${newVaultItem.type === type.id ? 'bg-amber-500 border-amber-500 text-slate-950 shadow-xl' : 'bg-slate-950 border-indigo-900/50 text-indigo-400 hover:border-amber-500/50'}`}>
                         <type.icon size={24} /><span className="text-[9px] uppercase font-bold tracking-widest">{type.label}</span>
                       </button>
                     ))}
                  </div>

                  <form onSubmit={handleAddVaultItem} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Asset Title</label>
                          <input required type="text" className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none" placeholder="Title of the collection" value={newVaultItem.title} onChange={e => setNewVaultItem({...newVaultItem, title: e.target.value})} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Investment Price ($)</label>
                          <input required type="number" className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none" value={newVaultItem.price} onChange={e => setNewVaultItem({...newVaultItem, price: parseInt(e.target.value)})} />
                        </div>
                        {newVaultItem.type === 'video' && (
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">YouTube URL</label>
                            <div className="relative">
                              <input required type="url" className="w-full bg-slate-950 border border-indigo-900 p-4 pl-12 text-white text-sm rounded-sm focus:border-amber-500 outline-none" placeholder="https://youtube.com/watch?v=..." value={newVaultItem.video_url} onChange={e => setNewVaultItem({...newVaultItem, video_url: e.target.value})} />
                              <Youtube className="absolute left-4 top-1/2 -translate-y-1/2 text-rose-500" size={20} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Short Description</label>
                        <textarea required className={`w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none ${newVaultItem.type === 'blog' ? 'h-[80px]' : 'h-[132px]'}`} placeholder="Describe this offering..." value={newVaultItem.description} onChange={e => setNewVaultItem({...newVaultItem, description: e.target.value})} />
                      </div>
                    </div>

                    {newVaultItem.type === 'blog' && (
                      <div className="space-y-4">
                         <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">Soul Wisdom (Content)</label>
                         <textarea required className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm h-[300px] focus:border-amber-500 outline-none" placeholder="Write your transmission here..." value={newVaultItem.content} onChange={e => setNewVaultItem({...newVaultItem, content: e.target.value})} />
                      </div>
                    )}

                    {newVaultItem.type !== 'video' && (
                      <div className="space-y-4 pt-4 border-t border-indigo-900/30">
                        <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold block mb-2">{newVaultItem.type === 'blog' ? 'Cover Image' : 'Celestial Gallery'}</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
                          {newVaultItem.media_urls.map((url, i) => (
                             <div key={i} className="relative aspect-square rounded-sm border border-indigo-900 overflow-hidden group">
                                <img src={url} className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImagePreview(i)} className="absolute inset-0 bg-rose-600/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"><Trash2 size={20} /></button>
                             </div>
                          ))}
                          {(newVaultItem.type === 'album' || newVaultItem.media_urls.length === 0) && (
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-sm border-2 border-dashed border-indigo-900 flex flex-col items-center justify-center gap-2 text-indigo-500 hover:border-amber-500 hover:text-amber-500 transition-all">
                               <UploadCloud size={24} /><span className="text-[8px] uppercase tracking-widest font-bold">Add Image</span>
                            </button>
                          )}
                        </div>
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple={newVaultItem.type === 'album'} accept="image/*" className="hidden" />
                      </div>
                    )}

                    <div className="flex justify-end gap-4 pt-6 border-t border-indigo-900/30">
                      <button type="button" onClick={() => setIsAddingVaultItem(false)} className="px-6 py-3 border border-indigo-800 text-indigo-400 text-[10px] font-bold uppercase">Cancel</button>
                      <button type="submit" disabled={isSavingVault} className="px-10 py-3 bg-amber-500 text-slate-950 text-[10px] font-bold uppercase rounded-sm shadow-xl flex items-center gap-2">
                         {isSavingVault ? <Loader2 size={16} className="animate-spin" /> : <><ShieldCheck size={16}/> Secure Asset</>}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {data.vault.map(item => (
                  <div key={item.id} className="bg-slate-900 border border-indigo-900/30 rounded-sm overflow-hidden flex flex-col group hover:border-amber-500/30 transition-all shadow-lg">
                    <div className="aspect-video bg-indigo-950/40 relative overflow-hidden">
                       {item.media_urls?.[0] ? (
                          <img src={item.media_urls[0]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                       ) : item.type === 'video' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-slate-950/40">
                             <Youtube size={40} className="text-rose-500" />
                             <span className="text-[8px] uppercase tracking-widest text-indigo-500">Video Link</span>
                          </div>
                       ) : (
                          <div className="w-full h-full flex items-center justify-center"><Layers size={40} className="text-indigo-900" /></div>
                       )}
                       <div className="absolute top-2 right-2 bg-slate-950/80 px-2 py-1 rounded text-[10px] font-bold text-amber-500">${item.price}</div>
                       <div className="absolute top-2 left-2 p-1.5 bg-slate-950/80 rounded-sm border border-white/10">
                          {item.type === 'blog' ? <FileText size={12} className="text-amber-400" /> : item.type === 'video' ? <Play size={12} className="text-rose-500" /> : <ImageIcon size={12} className="text-indigo-400" />}
                       </div>
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-serif text-white mb-2">{item.title}</h4>
                      <p className="text-xs text-indigo-200/60 mb-6 line-clamp-3 italic">"{item.description}"</p>
                      <div className="flex justify-between items-center text-[8px] uppercase tracking-widest text-indigo-600 font-bold">
                         <span>{item.type.toUpperCase()}</span>
                         <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="space-y-12 animate-fade-in">
               <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                 <div className="bg-slate-900 p-8 border border-indigo-900/30 rounded-sm hover:border-amber-500/30 transition-all group text-center"><p className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2">Seekers</p><p className="text-4xl font-serif">{data.members.length}</p></div>
                 <div className="bg-slate-900 p-8 border border-indigo-900/30 rounded-sm hover:border-amber-500/30 transition-all group text-center"><p className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2">Bookings</p><p className="text-4xl font-serif">{data.bookings.length}</p></div>
                 <div className="bg-slate-900 p-8 border border-indigo-900/30 rounded-sm hover:border-amber-500/30 transition-all group text-center"><p className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2">Transmissions</p><p className="text-4xl font-serif">{data.messages.length}</p></div>
                 <div className="bg-slate-900 p-8 border border-indigo-900/30 rounded-sm hover:border-amber-500/30 transition-all group text-center"><p className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2">Vault</p><p className="text-4xl font-serif">{data.vault.length}</p></div>
               </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 border-b border-indigo-900/30 pb-6"><div><h3 className="text-3xl font-serif text-white uppercase tracking-widest">Soul Registry</h3></div><div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" size={16} /><input type="text" value={memberSearch} onChange={e => setMemberSearch(e.target.value)} placeholder="Search registry..." className="w-full bg-slate-900 border border-indigo-900/50 pl-10 pr-4 py-3 text-xs uppercase tracking-widest text-indigo-100 rounded-sm focus:border-amber-500 outline-none" /></div></div>
              <div className="grid gap-4">{filteredMembers.map(member => (<div key={member.id} className="bg-slate-900 border border-indigo-900/30 p-5 rounded-sm flex items-center justify-between hover:bg-indigo-950/30 transition-all"><div className="flex items-center gap-6"><div className="w-12 h-12 rounded-full border border-amber-500/20 overflow-hidden bg-indigo-950 flex items-center justify-center">{member.profile_picture ? <img src={member.profile_picture} className="w-full h-full object-cover" /> : <UserIcon size={24} className="text-indigo-800" />}</div><div><h4 className="text-lg font-serif text-white">{member.name}</h4><p className="text-[10px] uppercase text-indigo-400 font-bold tracking-widest">{member.tier}</p></div></div><button onClick={() => handleViewMemberDetails(member)} className="px-5 py-2.5 bg-slate-950 border border-indigo-900 text-indigo-400 text-[10px] uppercase font-bold tracking-widest rounded-sm hover:border-amber-500 hover:text-white transition-all">Inspect</button></div>))}</div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="h-[calc(100vh-250px)] flex flex-col md:flex-row gap-6 animate-fade-in">
              <div className="w-full md:w-80 bg-slate-900 border border-indigo-900/50 rounded-sm overflow-hidden flex flex-col">
                <div className="p-4 border-b border-indigo-900/30"><h3 className="text-lg font-serif text-white uppercase tracking-widest">Transmissions</h3></div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">{conversations.length > 0 ? conversations.map(([id, msgs]) => { const member = data.members.find(m => m.email === id || m.id === id); const lastMsg = msgs[msgs.length - 1]; return (<button key={id} onClick={() => setActiveChatId(id)} className={`w-full p-4 border-b border-indigo-900/20 text-left hover:bg-indigo-950 transition-all flex items-center gap-4 ${activeChatId === id ? 'bg-indigo-950/60 border-l-4 border-l-amber-500' : ''}`}><div className="flex-1 min-0"><p className="text-sm font-serif text-white truncate">{member?.name || id}</p><p className="text-[10px] text-indigo-500 truncate">{lastMsg.content}</p></div></button>); }) : (<div className="p-10 text-center opacity-20"><MessageSquare size={32} className="mx-auto mb-2" /><p className="text-[10px] uppercase tracking-widest">No Transmissions Found</p></div>)}</div>
              </div>
              <div className="flex-1 bg-slate-900 border border-indigo-900/50 rounded-sm overflow-hidden flex flex-col shadow-2xl">
                {activeChatId ? (
                  <><div className="p-4 border-b border-indigo-900/30 flex justify-between items-center bg-slate-950/50"><div className="flex items-center gap-4"><h3 className="text-lg font-serif text-white">{data.members.find(m => m.email === activeChatId || m.id === activeChatId)?.name || activeChatId}</h3><div className="flex items-center gap-2"><button onClick={() => initiateCall('audio')} title="Start Audio Call" className="p-2 bg-indigo-950/40 border border-indigo-900 text-indigo-400 hover:text-amber-500 rounded-sm transition-all"><PhoneCall size={16}/></button><button onClick={() => initiateCall('video')} title="Start Video Call" className="p-2 bg-indigo-950/40 border border-indigo-900 text-indigo-400 hover:text-amber-500 rounded-sm transition-all"><VideoIcon size={16}/></button></div></div><button onClick={() => setActiveChatId(null)} className="p-2 text-indigo-500 hover:text-white"><X size={20}/></button></div><div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-950/30">{activeMessages.map((m, idx) => (<div key={idx} className={`flex ${m.sender_id === 'admin' ? 'justify-end' : 'justify-start'}`}><div className={`p-4 rounded-sm text-sm leading-relaxed ${m.sender_id === 'admin' ? 'bg-slate-800 text-amber-50 border border-amber-500/20' : 'bg-indigo-900 text-white'}`}>{m.content}</div></div>))}<div ref={chatEndRef} /></div><form onSubmit={handleReply} className="p-4 bg-slate-900 border-t border-indigo-900/50 flex gap-4"><input type="text" value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Compose celestial reply..." className="flex-1 bg-slate-950 border border-indigo-900/50 p-4 text-indigo-100 rounded-sm focus:outline-none focus:border-amber-500" /><button type="submit" disabled={isSendingReply || !replyText.trim()} className="bg-amber-500 hover:bg-amber-400 text-slate-950 px-8 rounded-sm font-bold uppercase text-[10px] tracking-widest transition-all shadow-lg">{isSendingReply ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button></form></>
                ) : (<div className="flex-1 flex flex-col items-center justify-center opacity-30 text-center p-12"><MessageCircle size={64} className="mb-6 text-indigo-500" /><h4 className="text-xl font-serif text-white uppercase tracking-widest mb-2">Registry Conversations</h4><p className="text-xs uppercase tracking-widest text-indigo-400">Select a seeker to view transmissions.</p></div>)}
              </div>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-6 animate-fade-in"><h3 className="text-3xl font-serif text-white mb-6 uppercase tracking-widest">Sacred Alignments</h3><div className="grid gap-6">{data.bookings.map(booking => (<div key={booking.id} className={`bg-slate-900 border-2 rounded-sm p-6 flex items-center justify-between transition-all ${booking.session_active ? 'border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.2)]' : 'border-indigo-900/30'}`}><div className="flex items-center gap-6"><div className="p-4 rounded-full bg-indigo-950/40 text-amber-500">{booking.commMethod === 'video' ? <Video size={24}/> : <Phone size={24}/>}</div><div><h4 className="text-lg font-serif text-white">{booking.name}</h4><p className="text-xs text-indigo-300">{booking.appointment_date} @ {booking.appointment_time}</p></div></div><div className="flex items-center gap-4">{booking.session_active ? (<button onClick={() => handleSessionAction(booking, 'end')} className="px-6 py-3 bg-rose-600 hover:bg-rose-500 text-white text-[10px] uppercase font-bold rounded-sm shadow-lg">End Session</button>) : (<button disabled={booking.status === 'completed'} onClick={() => handleSessionAction(booking, 'start')} className={`px-6 py-3 text-[10px] uppercase font-bold rounded-sm transition-all ${booking.status === 'completed' ? 'opacity-30 cursor-not-allowed text-indigo-50' : 'bg-amber-500 text-slate-950 hover:bg-amber-400'}`}>Initiate Channel</button>)}</div></div>))}</div></div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;